import { translateStatus, translateVehicleType, type Shipment } from './api'
import { getEffectiveShipmentStatus, isActiveStatus } from './managerOperational'
import { extractCitiesFromRoute, getRouteDisplayLabel } from './routeUtils'
import { parseRouteData as parseRouteJson, type ShipmentRouteData } from './routeData'

export type CabinetOrder = {
  id: string
  number: string
  route: string
  shortRoute: string
  status: string
  createdAt: string
  updatedAt: string
  completedAt?: string
  driverName?: string
  vehicleLabel?: string
  vehicleNumber?: string
}

export type CabinetMetrics = {
  total: number
  active: number
  inTransit: number
  awaitingStart: number
  completed: number
}

export type CabinetMonthlyBucket = {
  label: string
  total: number
}

export type CabinetStatusBucket = {
  key: string
  label: string
  count: number
  colorClassName: string
}

export type CabinetRouteBucket = {
  route: string
  count: number
}

export type CabinetRecentUpdate = {
  id: string
  orderNumber: string
  route: string
  status: string
  statusLabel: string
  updatedAt: string
}

export type CabinetSnapshot = {
  allOrders: CabinetOrder[]
  activeOrders: CabinetOrder[]
  historyOrders: CabinetOrder[]
  metrics: CabinetMetrics
  monthlyActivity: CabinetMonthlyBucket[]
  statusDistribution: CabinetStatusBucket[]
  popularRoutes: CabinetRouteBucket[]
  recentUpdates: CabinetRecentUpdate[]
  averageCompletionDays: number | null
}

const STATUS_COLOR_MAP: Record<string, string> = {
  created: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-orange-50 text-orange-700 border-orange-200',
  assigned: 'bg-violet-50 text-violet-700 border-violet-200',
  awaiting_driver: 'bg-orange-50 text-orange-700 border-orange-200',
  driver_rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
}

function parseShipmentRouteData(shipment: Shipment): ShipmentRouteData | null {
  return parseRouteJson(shipment.route_json)
}

function formatGeneratedOrderNumber(prefix: string, date: Date, suffix: string) {
  const year = date.getFullYear().toString().slice(-2)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${prefix}-${year}${month}${day}-${suffix}`
}

function normalizeShortRoute(route: string) {
  const cities = extractCitiesFromRoute(route)

  if (cities.length >= 2) {
    return `${cities[0]} → ${cities[cities.length - 1]}`
  }

  if (cities.length === 1) {
    return cities[0]
  }

  return route
}

function getFallbackRoute(orderId: string) {
  return `Маршрут по заказу ${orderId.slice(-6).toUpperCase()}`
}

function getRouteUpdatedAt(shipment: Shipment, routeData: ShipmentRouteData | null) {
  const candidates = [routeData?.loading_date, routeData?.unloading_date, shipment.planned_delivery_time, shipment.created_at]
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value))
    .filter((value) => !Number.isNaN(value.getTime()))
    .sort((left, right) => right.getTime() - left.getTime())

  return candidates[0]?.toISOString() ?? shipment.created_at
}

function normalizeBackendShipment(shipment: Shipment): CabinetOrder {
  const status = getEffectiveShipmentStatus(shipment)
  const routeData = parseShipmentRouteData(shipment)
  const sourceRoute = routeData?.route?.trim() || getFallbackRoute(shipment.id)
  const route = getRouteDisplayLabel(sourceRoute) || sourceRoute
  const createdAt = shipment.created_at
  const updatedAt = getRouteUpdatedAt(shipment, routeData)
  const completedAt = status === 'completed' || status === 'delivered' ? updatedAt : undefined

  return {
    id: shipment.id,
    number: routeData?.order_number?.trim() || formatGeneratedOrderNumber('ЗАКАЗ', new Date(createdAt), shipment.id.slice(-3).toUpperCase()),
    route,
    shortRoute: normalizeShortRoute(route),
    status,
    createdAt,
    updatedAt,
    completedAt,
    driverName: routeData?.driver_name?.trim() || undefined,
    vehicleLabel: routeData?.vehicle_type ? translateVehicleType(routeData.vehicle_type) : undefined,
    vehicleNumber: routeData?.vehicle_number?.trim() || undefined,
  }
}

export function getOrderStatusMeta(status: string) {
  return {
    label: translateStatus(status),
    className: STATUS_COLOR_MAP[status] || 'bg-slate-100 text-slate-700 border-slate-200',
  }
}

// Same set as the server's models.IsActiveEffectiveStatus: a driver's refusal stays active, waiting
// for another driver.
export function isActiveOrder(order: CabinetOrder) {
  return isActiveStatus(order.status)
}

export function isInTransitOrder(order: CabinetOrder) {
  return order.status === 'in_progress'
}

export function isAwaitingStartOrder(order: CabinetOrder) {
  return ['created', 'pending', 'assigned', 'awaiting_driver', 'driver_rejected', 'accepted'].includes(order.status)
}

export function isCompletedOrder(order: CabinetOrder) {
  return ['completed', 'delivered'].includes(order.status)
}

export function formatOrderDate(value: string, withTime = false) {
  const parsedValue = new Date(value)
  if (Number.isNaN(parsedValue.getTime())) return 'Не указано'

  return parsedValue.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
}

export function formatOrderCompactDate(value: string) {
  const parsedValue = new Date(value)
  if (Number.isNaN(parsedValue.getTime())) return 'Не указано'

  return parsedValue.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
  })
}

export function buildCabinetSnapshot(shipments: Shipment[]): CabinetSnapshot {
  const allOrders = shipments
    .map((shipment) => normalizeBackendShipment(shipment))
    .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())

  const activeOrders = allOrders.filter(isActiveOrder)
  // The history holds everything archived: finished and cancelled.
  const historyOrders = allOrders
    .filter((order) => !isActiveOrder(order))
    .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())

  const metrics: CabinetMetrics = {
    total: allOrders.length,
    active: activeOrders.length,
    inTransit: allOrders.filter(isInTransitOrder).length,
    awaitingStart: allOrders.filter(isAwaitingStartOrder).length,
    completed: allOrders.filter(isCompletedOrder).length,
  }

  const monthlyActivity = Array.from({ length: 6 }, (_, index) => {
    const now = new Date()
    const bucketDate = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1)
    const nextDate = new Date(now.getFullYear(), now.getMonth() - (4 - index), 1)
    const ordersInMonth = allOrders.filter((order) => {
      const createdAt = new Date(order.createdAt)
      return createdAt >= bucketDate && createdAt < nextDate
    })

    return {
      label: bucketDate.toLocaleDateString('ru-RU', { month: 'short' }),
      total: ordersInMonth.length,
    }
  })

  const statusKeys = ['created', 'awaiting_driver', 'driver_rejected', 'assigned', 'accepted', 'in_progress', 'completed']
  const statusDistribution = statusKeys
    .map((key) => ({
      key,
      label: translateStatus(key),
      count: allOrders.filter((order) => {
        if (key === 'completed') {
          return isCompletedOrder(order)
        }

        if (key === 'created') {
          return order.status === 'created' || order.status === 'pending'
        }

        return order.status === key
      }).length,
      colorClassName: key === 'in_progress'
        ? 'bg-blue-500'
        : key === 'completed'
          ? 'bg-emerald-500'
          : key === 'assigned' || key === 'accepted'
          ? 'bg-violet-500'
            : key === 'awaiting_driver' || key === 'driver_rejected'
              ? 'bg-orange-500'
              : 'bg-amber-500',
    }))
    .filter((bucket) => bucket.count > 0)

  const routeCounter = new Map<string, number>()
  allOrders.forEach((order) => {
    routeCounter.set(order.shortRoute, (routeCounter.get(order.shortRoute) || 0) + 1)
  })

  const popularRoutes = Array.from(routeCounter.entries())
    .map(([route, count]) => ({ route, count }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 5)

  const recentUpdates = allOrders.slice(0, 5).map((order) => ({
    id: order.id,
    orderNumber: order.number,
    route: order.shortRoute,
    status: order.status,
    statusLabel: translateStatus(order.status),
    updatedAt: order.updatedAt,
  }))

  const completedOrdersWithDates = historyOrders.filter((order) => order.completedAt)
  const averageCompletionDays = completedOrdersWithDates.length > 0
    ? Number((
      completedOrdersWithDates.reduce((sum, order) => {
        const createdAt = new Date(order.createdAt).getTime()
        const completedAt = new Date(order.completedAt as string).getTime()
        const diffDays = Math.max(0, (completedAt - createdAt) / (1000 * 60 * 60 * 24))
        return sum + diffDays
      }, 0) / completedOrdersWithDates.length
    ).toFixed(1))
    : null

  return {
    allOrders,
    activeOrders,
    historyOrders,
    metrics,
    monthlyActivity,
    statusDistribution,
    popularRoutes,
    recentUpdates,
    averageCompletionDays,
  }
}
